import {
  DEFAULT_UNIT_MS,
  MAX_DISMISSALS,
  quietMsFor,
  type NagState,
} from './updateNagState';

/**
 * Dónde se recuerda la escalera del aviso de actualización.
 *
 * ─── Por qué `localStorage` y no el nivel duradero ─────────────────────────
 *
 * `nativeDurableStorage` tiene niveles más fuertes (Preferences, IndexedDB)
 * pero son para lo que NO se puede perder: la sesión y el trabajo del
 * operario. Esto no es trabajo de nadie. Si Android desaloja `localStorage`
 * por falta de disco, lo peor que pasa es que la escalera vuelva a empezar —
 * molesto, no grave. Gastar el nivel caro en un dato barato sería un error.
 *
 * Sobrevive al OTA porque el origen del WebView sigue siendo
 * `https://localhost` después de recargar.
 *
 * ─── Por qué todo va en try/catch ──────────────────────────────────────────
 *
 * `localStorage` LANZA, no devuelve null: almacenamiento bloqueado por
 * política del dispositivo, cuota llena, WebView en un estado raro. Si una de
 * esas excepciones sube, se lleva por delante el aviso de actualización
 * entero. Degradar a "sin memoria" es peor que recordar y muchísimo mejor que
 * no avisar.
 */

export const NAG_KEY = 'rois.update.nag';

/**
 * Cuánto vale una unidad de la escalera, en milisegundos.
 *
 * Existe para poder recorrer LA MISMA escalera en 24 minutos en vez de en 24
 * horas al probarla en tablet. No se usa en producción.
 */
export const NAG_UNIT_KEY = 'rois.update.nagUnitMs';

/** Lo guardado: el estado más hasta cuándo callarse. */
interface StoredNag extends NagState {
  quietUntil: number | null;
  /**
   * La versión que CORRÍA la tablet cuando empezó a contar el plazo. Es lo que
   * dice si la tablet se ha actualizado desde entonces: si ya no corre esa, el
   * reloj vuelve a cero.
   */
  running: string;
}

function nuevo(version: string, now: number, running: string): StoredNag {
  return { version, firstSeen: now, dismissals: 0, quietUntil: null, running };
}

/** `unknown` → `StoredNag` válido, o `null` si no lo es. */
function parse(raw: string | null): StoredNag | null {
  if (!raw) return null;
  try {
    const v = JSON.parse(raw) as unknown;
    if (typeof v !== 'object' || v === null || Array.isArray(v)) return null;
    const o = v as Record<string, unknown>;
    // Se comprueba campo a campo a propósito: un estado a medias es peor que
    // ninguno, porque produce decisiones con números que no significan nada.
    if (typeof o.version !== 'string') return null;
    if (typeof o.running !== 'string') return null;
    if (typeof o.firstSeen !== 'number' || !Number.isFinite(o.firstSeen)) return null;
    if (typeof o.dismissals !== 'number' || !Number.isFinite(o.dismissals)) return null;
    const quietUntil =
      typeof o.quietUntil === 'number' && Number.isFinite(o.quietUntil)
        ? o.quietUntil
        : null;
    return {
      version: o.version,
      firstSeen: o.firstSeen,
      dismissals: o.dismissals,
      quietUntil,
      running: o.running,
    };
  } catch {
    return null;
  }
}

function write(value: StoredNag): void {
  try {
    localStorage.setItem(NAG_KEY, JSON.stringify(value));
  } catch {
    // Sin memoria el aviso sigue saliendo, solo que desde el primer escalón.
  }
}

/**
 * Lee el estado de este aviso. Nunca lanza.
 *
 * Tres casos, y el del medio es el que importa:
 *
 *  - **Nada guardado, o la tablet se ha actualizado** (ya no corre la versión
 *    con la que empezó a contar): de cero.
 *  - **Ha salido una versión más nueva, pero la tablet sigue sin actualizarse**:
 *    los cierres vuelven a cero —el operario no ha rechazado esta versión
 *    todavía— pero **el reloj del plazo se conserva**. La regla es "24 h sin
 *    actualizarse", y la tablet sigue sin actualizarse. Si cada release pusiera
 *    el reloj a cero, sacando versiones más a menudo que cada 24 h —en beta
 *    pasa— una tablet que cierra el aviso no llegaría nunca al plazo.
 *  - **Misma versión**: tal cual.
 */
export function readNag(
  version: string,
  now: number,
  running: string
): { state: NagState; quietUntil: number | null } {
  let stored: StoredNag | null = null;
  try {
    stored = parse(localStorage.getItem(NAG_KEY));
  } catch {
    stored = null;
  }

  let actual: StoredNag;
  if (!stored || stored.running !== running) {
    actual = nuevo(version, now, running);
    write(actual);
  } else if (stored.version !== version) {
    actual = {
      version,
      firstSeen: stored.firstSeen,
      dismissals: 0,
      quietUntil: null,
      running,
    };
    write(actual);
  } else {
    actual = stored;
  }

  return {
    state: {
      version: actual.version,
      firstSeen: actual.firstSeen,
      dismissals: actual.dismissals,
    },
    quietUntil: actual.quietUntil,
  };
}

/** Apunta un cierre y calcula hasta cuándo callarse. Nunca lanza. */
export function recordDismissal(
  version: string,
  now: number,
  unitMs: number,
  running: string
): void {
  const { state } = readNag(version, now, running);
  const dismissals = state.dismissals + 1;
  write({
    version,
    firstSeen: state.firstSeen,
    dismissals,
    quietUntil: now + quietMsFor(dismissals, unitMs),
    running,
  });
}

/** Un número positivo y finito, o `null` si el texto no lo es. */
function positiveNumber(raw: string | null | undefined): number | null {
  if (!raw) return null;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * Cuánto vale una unidad. En producción, una hora; se puede bajar a minutos
 * para probar la escalera entera en 24 minutos.
 *
 * Por orden: `localStorage` (app de prueba, desde DevTools) → la compilada con
 * `VITE_UPDATE_NAG_UNIT_MS` → una hora.
 *
 * 🔴 La compilada existe porque en la app OFICIAL no hay DevTools: la única
 * forma de probar la escalera en minutos en una release real es hornearla en
 * el bundle (casilla `testLadder` del workflow). Esa release acosa a todo su
 * canal cada pocos minutos; la siguiente release normal lo deshace.
 */
export function resolveUnitMs(): number {
  try {
    const stored = positiveNumber(localStorage.getItem(NAG_UNIT_KEY));
    if (stored) return stored;
  } catch {
    // Sin `localStorage` se sigue con la compilada.
  }
  return positiveNumber(import.meta.env.VITE_UPDATE_NAG_UNIT_MS) ?? DEFAULT_UNIT_MS;
}

/**
 * Cuántos cierres se permiten antes de bloquear. En producción,
 * `MAX_DISMISSALS`; una build de prueba lo baja con
 * `VITE_UPDATE_NAG_MAX_DISMISSALS` para llegar antes al bloqueo.
 */
export function resolveMaxDismissals(): number {
  const n = positiveNumber(import.meta.env.VITE_UPDATE_NAG_MAX_DISMISSALS);
  return n ? Math.floor(n) : MAX_DISMISSALS;
}
